import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRightLeft, Landmark, Clock,
  CheckCircle2, AlertCircle, Loader2,
  ChevronLeft, ChevronRight, CalendarClock,
} from 'lucide-react';
import { withdrawalAPI } from '../../api/withdrawal';
import { Card } from '../../components/ui/Card';
import PageHeader from '../../components/ui/PageHeader';

// ── Constants ────────────────────────────────────────────────
const FILTER_TABS = [
  { value: 'all',         label: 'All' },
  { value: 'pending',     label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed',   label: 'Completed' },
  { value: 'failed',      label: 'Failed' },
];

const TRANSFER_METHODS = {
  bank_transfer: 'Bank Transfer',
  neft:          'NEFT',
  rtgs:          'RTGS',
  upi:           'UPI',
  cheque:        'Cheque',
  cash:          'Cash',
};

const TRANSFER_STATUS_CFG = {
  pending:     { color: '#fcd34d', bg: 'rgba(245,158,11,0.18)',  label: 'Pending',     Icon: Clock },
  in_progress: { color: '#93c5fd', bg: 'rgba(59,130,246,0.18)', label: 'In Progress', Icon: Loader2 },
  completed:   { color: '#6ee7b7', bg: 'rgba(5,150,105,0.18)',  label: 'Completed',   Icon: CheckCircle2 },
  failed:      { color: '#fca5a5', bg: 'rgba(239,68,68,0.18)',  label: 'Failed',      Icon: AlertCircle },
};

// ── Helpers ──────────────────────────────────────────────────
const fmt = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

const fmtDate = (val) => {
  if (!val) return '—';
  return new Date(val).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

const periodMonths = (period) => {
  if (!period) return null;
  const n = parseInt(period.split('_')[0], 10);
  return isNaN(n) ? null : n;
};

/**
 * Calculates deadline from requested_at + withdrawal_period months.
 * Returns { deadline, remainingDays, isOverdue, isDueToday, isDueSoon }
 */
const getDeadlineInfo = (requestedAt, withdrawalPeriod) => {
  const months = periodMonths(withdrawalPeriod);
  if (!requestedAt || months === null) return null;

  const deadline = new Date(requestedAt);
  deadline.setMonth(deadline.getMonth() + months);
  deadline.setHours(23, 59, 59, 999);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const remainingDays = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));

  return {
    deadline,
    remainingDays,
    isOverdue:  remainingDays < 0,
    isDueToday: remainingDays === 0,
    isDueSoon:  remainingDays > 0 && remainingDays <= 7,
  };
};

// ── Sub-components ────────────────────────────────────────────
function TransferStatusBadge({ status }) {
  const cfg = TRANSFER_STATUS_CFG[status];
  if (!cfg) return null;
  const { Icon } = cfg;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold"
      style={{ background: cfg.bg, color: cfg.color }}
    >
      <Icon size={10} />
      {cfg.label}
    </span>
  );
}

function DeadlineBadge({ requestedAt, withdrawalPeriod, transferStatus }) {
  const done = transferStatus === 'completed' || transferStatus === 'failed';
  const info = getDeadlineInfo(requestedAt, withdrawalPeriod);
  if (!info) return null;

  let bg, color, text;
  if (done) {
    bg    = 'rgba(255,255,255,0.08)';
    color = 'rgba(255,255,255,0.4)';
    text  = `Due ${fmtDate(info.deadline)}`;
  } else if (info.isOverdue) {
    bg    = 'rgba(239,68,68,0.18)';
    color = '#fca5a5';
    text  = `${Math.abs(info.remainingDays)}d overdue`;
  } else if (info.isDueToday) {
    bg    = 'rgba(245,158,11,0.25)';
    color = '#fcd34d';
    text  = 'Due today';
  } else if (info.isDueSoon) {
    bg    = 'rgba(245,158,11,0.15)';
    color = '#fbbf24';
    text  = `${info.remainingDays}d left`;
  } else {
    bg    = 'rgba(5,150,105,0.15)';
    color = '#6ee7b7';
    text  = `${info.remainingDays}d left`;
  }

  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold"
      style={{ background: bg, color }}
    >
      <Clock size={9} />
      {text}
    </span>
  );
}

function TransferCard({ transfer, index }) {
  const info        = getDeadlineInfo(transfer.requested_at, transfer.withdrawal_period);
  const methodLabel = TRANSFER_METHODS[transfer.transfer_method];
  const months      = periodMonths(transfer.withdrawal_period);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Card className="flex flex-col gap-3">

        {/* Amount + period */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg,#d4a017,#f0c040)' }}
            >
              <ArrowRightLeft size={16} color="white" />
            </div>
            <div>
              <p className="text-base font-bold font-serif text-white">{fmt(transfer.amount)}</p>
              <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.45)' }}>
                {months ? `${months} Month${months > 1 ? 's' : ''} Plan` : '—'}
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <TransferStatusBadge status={transfer.transfer_status} />
            <DeadlineBadge
              requestedAt={transfer.requested_at}
              withdrawalPeriod={transfer.withdrawal_period}
              transferStatus={transfer.transfer_status}
            />
          </div>
        </div>

        {/* Deadline row */}
        <div
          className="flex items-center gap-2 rounded-2xl px-3 py-2.5"
          style={{ background: 'rgba(212,160,23,0.07)', border: '1px solid rgba(212,160,23,0.18)' }}
        >
          <CalendarClock size={13} style={{ color: '#d4a017', flexShrink: 0 }} />
          <div className="flex-1 flex items-center justify-between">
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>
              Transfer deadline
            </span>
            <span className="text-xs font-semibold" style={{ color: '#f0c040' }}>
              {info ? fmtDate(info.deadline) : '—'}
            </span>
          </div>
        </div>

        {/* Transfer method */}
        {methodLabel && (
          <div className="flex items-center gap-2">
            <Landmark size={12} style={{ color: 'rgba(255,255,255,0.35)', flexShrink: 0 }} />
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>
              Transfer via&nbsp;
              <span className="font-semibold" style={{ color: 'rgba(255,255,255,0.75)' }}>
                {methodLabel}
              </span>
            </span>
          </div>
        )}

        {/* Notes */}
        {transfer.transfer_notes && (
          <p
            className="text-xs italic rounded-xl px-3 py-2"
            style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.5)' }}
          >
            {transfer.transfer_notes}
          </p>
        )}

        {/* Footer dates */}
        <div className="flex items-center justify-between pt-0.5">
          <p className="text-[11px]" style={{ color: 'rgba(255,255,255,0.3)' }}>
            Requested: {fmtDate(transfer.requested_at)}
          </p>
          {transfer.transferred_at && (
            <p className="text-[11px]" style={{ color: '#6ee7b7' }}>
              Transferred: {fmtDate(transfer.transferred_at)}
            </p>
          )}
        </div>

      </Card>
    </motion.div>
  );
}

function CardSkeleton() {
  return (
    <div className="card flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <div className="skeleton w-10 h-10 rounded-2xl flex-shrink-0" />
        <div className="flex-1 flex flex-col gap-2">
          <div className="skeleton h-4 w-24 rounded" />
          <div className="skeleton h-3 w-16 rounded" />
        </div>
        <div className="skeleton h-5 w-20 rounded-full" />
      </div>
      <div className="skeleton h-10 rounded-2xl" />
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────
export default function WithdrawalTransferPage() {
  const [activeFilter, setActiveFilter] = useState('all');
  const [transfers,    setTransfers]    = useState([]);
  const [pagination,   setPagination]   = useState(null);
  const [page,         setPage]         = useState(1);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');

  const filterTimer = useRef(null);

  const fetchTransfers = useCallback(async (pg = 1) => {
    setLoading(true);
    setError('');
    try {
      const params = { page: pg, per_page: 10 };
      if (activeFilter !== 'all') params.transfer_status = activeFilter;
      const res = await withdrawalAPI.getTransfers(params);
      if (res.data.status) {
        setTransfers(res.data.data.transfers);
        setPagination(res.data.data.pagination);
        setPage(pg);
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load transfer records.');
    } finally {
      setLoading(false);
    }
  }, [activeFilter]);

  // Re-fetch when filter changes, reset page to 1
  useEffect(() => {
    clearTimeout(filterTimer.current);
    filterTimer.current = setTimeout(() => fetchTransfers(1), 100);
    return () => clearTimeout(filterTimer.current);
  }, [fetchTransfers]);

  const handleFilterChange = (val) => {
    setActiveFilter(val);
    setPage(1);
  };

  // ── TAB_ACTIVE / INACTIVE styles matching WithdrawPage ───
  const tabActive   = { background: 'linear-gradient(135deg,#d4a017,#f0c040)', color: '#fff', boxShadow: '0 2px 10px rgba(212,160,23,0.3)' };
  const tabInactive = { color: 'rgba(255,255,255,0.5)' };

  const empty = !loading && !error && transfers.length === 0;

  return (
    <div>
      <PageHeader
        title="Transfer Tracking"
        subtitle="Track your withdrawal transfers"
        showBack
      />

      <div className="px-4 pt-2 pb-6 flex flex-col gap-4">

        {/* ── Filter tabs ── */}
        <div
          className="flex gap-1 rounded-2xl p-1 overflow-x-auto"
          style={{ background: 'rgba(255,255,255,0.08)' }}
        >
          {FILTER_TABS.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => handleFilterChange(value)}
              className="flex-shrink-0 px-3 py-2 text-xs font-semibold rounded-xl transition-all whitespace-nowrap"
              style={activeFilter === value ? tabActive : tabInactive}
            >
              {label}
            </button>
          ))}
        </div>

        {/* ── Error ── */}
        {error && (
          <Card>
            <div className="flex items-center gap-2 py-4 justify-center">
              <AlertCircle size={16} style={{ color: '#fca5a5' }} />
              <p className="text-sm" style={{ color: '#fca5a5' }}>{error}</p>
            </div>
          </Card>
        )}

        {/* ── Loading skeletons ── */}
        <AnimatePresence>
          {loading && !error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col gap-3"
            >
              {[1, 2, 3].map((i) => <CardSkeleton key={i} />)}
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Transfer list ── */}
        {!loading && !error && transfers.length > 0 && (
          <div className="flex flex-col gap-3">
            {transfers.map((t, idx) => (
              <TransferCard key={t.id} transfer={t} index={idx} />
            ))}
          </div>
        )}

        {/* ── Empty state ── */}
        {empty && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="flex flex-col items-center gap-3 py-12">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(212,160,23,0.1)' }}
              >
                <ArrowRightLeft size={24} style={{ color: 'rgba(212,160,23,0.5)' }} />
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-white">
                  {activeFilter === 'all'
                    ? 'No transfer records yet'
                    : `No ${FILTER_TABS.find(f => f.value === activeFilter)?.label.toLowerCase()} transfers`}
                </p>
                <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  {activeFilter === 'all'
                    ? 'Approved withdrawals will appear here once the admin processes them'
                    : 'Try a different filter to see your records'}
                </p>
              </div>
              {activeFilter !== 'all' && (
                <button
                  onClick={() => handleFilterChange('all')}
                  className="text-xs font-semibold"
                  style={{ color: '#f0c040' }}
                >
                  View all transfers
                </button>
              )}
            </Card>
          </motion.div>
        )}

        {/* ── Pagination ── */}
        {!loading && pagination && pagination.last_page > 1 && (
          <div className="flex items-center justify-between">
            <button
              disabled={page === 1}
              onClick={() => fetchTransfers(page - 1)}
              className="flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded-xl transition-all active:scale-95 disabled:opacity-30"
              style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.7)' }}
            >
              <ChevronLeft size={14} /> Prev
            </button>
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
              {page} / {pagination.last_page}
            </p>
            <button
              disabled={page === pagination.last_page}
              onClick={() => fetchTransfers(page + 1)}
              className="flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded-xl transition-all active:scale-95 disabled:opacity-30"
              style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.7)' }}
            >
              Next <ChevronRight size={14} />
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
